import type { components, paths } from "@/api-schema";
import {
  DmsSystem,
  PathsV1CustomerSapExportSearchPostParametersQueryExtend,
  PathsV1CustomerSapExportSearchPostParametersQueryOrderByDirection,
  PathsV1CustomerSapExportSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useCustomerSapExportDeleteMutation } from "@/common/api/customer-sap-export/use-customer-sap-export-delete.mutation";
import {
  CustomerSapExportReExportBody,
  useCustomerSapExportReExportMutation,
} from "@/common/api/customer-sap-export/use-customer-sap-export-re-export.mutation";
import { useCustomerSapExportSearchQuery } from "@/common/api/customer-sap-export/use-customer-sap-export-search.query";
import { buildCustomerSapExportSearchBody } from "@/common/helpers/build-customer-sap-export-search-body";
import { hasCustomerSapExportSearchCriteria } from "@/common/helpers/has-customer-sap-export-search-criteria";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import type { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import type { TableFeatures } from "@/components/data-table/types";
import { buildDetailTabSearch } from "@/components/detail-page/_helpers/detail-tab-search";
import { CustomerIdentifierTypeFilter } from "@/components/filter/predefined/customer-identifier-type-filter";
import { SapExportErrorCategoryFilter } from "@/components/filter/predefined/sap-export-error-category-filter/sap-export-error-category-filter";
import { SapExportPeriodFilter } from "@/components/filter/predefined/sap-export-export-period-filter";
import { SapExportStatusFilter } from "@/components/filter/predefined/sap-export-status-filter";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { type RowAction } from "@/components/ui/table-row-actions";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CustomerCompanyGroupScopeEmptyState } from "@/modules/masterdata/customer/_components/customer-company-group-scope-empty-state";
import { hasCustomerCompanyGroupScope } from "@/modules/masterdata/customer/customer.permissions";
import { userConfigStore } from "@/store/user-config-store";
import { userStore } from "@/store/user-store";
import { SAP_EXPORT_TAB_ID } from "@/views/customer-detail/tabs/sap-export-tab/sap-export-tab";
import i18next from "i18next";
import { CloudUploadIcon, UserRoundIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { createBasePredefinedTable } from "../_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "../_components/base-predefined-table.types";
import { getCustomerDisplayName } from "../customer-table/_helpers/get-customer-display-name";
import { customerSapExportTableDefaultColumns } from "./customer-sap-export-table-columns";

type CustomerSapExport = components["schemas"]["CustomerSapExport"] & RowWithIdentifier;
type CustomerSapExportSorting = NonNullable<
  paths["/v1/customer-sap-export/search"]["post"]["parameters"]["query"]["orderBy"]
>;
type CustomerSapExportQueryParams = paths["/v1/customer-sap-export/search"]["post"]["parameters"]["query"];
type CustomerSapExportRequestBody =
  paths["/v1/customer-sap-export/search"]["post"]["requestBody"]["content"]["application/json"];
type CustomerSapExportExtend = NonNullable<
  paths["/v1/customer-sap-export/search"]["post"]["parameters"]["query"]["extend"]
>;

const DEFAULT_EXTEND: CustomerSapExportExtend = [
  PathsV1CustomerSapExportSearchPostParametersQueryExtend.Customer,
  PathsV1CustomerSapExportSearchPostParametersQueryExtend.Customer_CustomerIdentifiers,
] as const;

const DEFAULT_COLUMNS = [
  customerSapExportTableDefaultColumns.columnCustomerName,
  customerSapExportTableDefaultColumns.columnIds,
  customerSapExportTableDefaultColumns.columnStatus,
  customerSapExportTableDefaultColumns.columnCategory,
  customerSapExportTableDefaultColumns.columnError,
  customerSapExportTableDefaultColumns.columnLastExportAt,
];

const CUSTOMER_SAP_EXPORT_TABLE_CONFIG: PredefinedTableConfig<CustomerSapExportSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1CustomerSapExportSearchPostParametersQueryOrderByDirection.Desc,
      field: PathsV1CustomerSapExportSearchPostParametersQueryOrderByField.LastExportAt,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("masterdata:SearchSapExports"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: false,
    showView: false,
    showDelete: true,
  } as const,
  sortingMapping: {
    customerName: ["customerName"],
    exportStatus: ["exportStatus"],
    category: ["errorCategory"],
    lastExportAt: ["lastExportAt"],
  } as const,
} as const;

const CUSTOMER_SAP_EXPORT_TABLE_QUERY_HOOKS: DataFetchHooks<
  CustomerSapExport,
  CustomerSapExportQueryParams,
  CustomerSapExportRequestBody
> = {
  useSearchQuery: useCustomerSapExportSearchQuery,
  useDeleteMutation: useCustomerSapExportDeleteMutation,
} as const;

export interface CustomerSapExportTableProps extends BasePredefinedTableProps<
  CustomerSapExport,
  CustomerSapExportSorting,
  CustomerSapExportQueryParams,
  CustomerSapExportRequestBody
> {
  extend?: CustomerSapExportExtend;
}

const BaseCustomerSapExportTable = createBasePredefinedTable({
  config: CUSTOMER_SAP_EXPORT_TABLE_CONFIG,
  queryHooks: CUSTOMER_SAP_EXPORT_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function CustomerSapExportTable(props: CustomerSapExportTableProps) {
  const [, navigate] = useLocation();
  const { t } = useTranslation(["masterdata", "common"]);
  const ability = useAbility();
  const deleteMutation = useCustomerSapExportDeleteMutation();
  const reExportMutation = useCustomerSapExportReExportMutation();
  const dateFormat = userConfigStore((state) => state.dateFormat);
  const userRoles = userStore((state) => state.user?.roles);
  const canManageCustomer = ability.can("manage", "Customer");
  const hasCompanyGroupScope = hasCustomerCompanyGroupScope(userRoles);

  const {
    name = "customer-sap-export-table",
    body: externalBody,
    extend = DEFAULT_EXTEND,
    filters,
    filterValues: externalFilterValues,
    onFilterChange: externalOnFilterChange,
    onSearchChange: externalOnSearchChange,
    headerComponent: externalHeaderComponent,
    onRowNavigate = (customerSapExport) => {
      if (!customerSapExport.customer) {
        return undefined;
      }

      return `~/masterdata/customer/${customerSapExport.customer.uuid}${buildDetailTabSearch(SAP_EXPORT_TAB_ID)}`;
    },
    ...restProps
  } = props;
  const isControlled = externalFilterValues !== undefined;
  const [internalFilterValues, setInternalFilterValues] = useState<Record<string, string[]>>(
    externalFilterValues ?? {},
  );
  const filterValues = isControlled ? externalFilterValues : internalFilterValues;
  const [search, setSearch] = useState(props.query?.search ?? "");

  const handleFilterChange = useCallback(
    (filterId: string, values: string[]): void => {
      if (!isControlled) {
        setInternalFilterValues((currentFilterValues) => ({
          ...currentFilterValues,
          [filterId]: values,
        }));
      }
      externalOnFilterChange?.(filterId, values);
    },
    [externalOnFilterChange, isControlled],
  );

  const handleSearchChange = useCallback(
    (value: string): void => {
      setSearch(value);
      externalOnSearchChange?.(value);
    },
    [externalOnSearchChange],
  );

  const defaultFilters = useMemo(
    () =>
      filters ?? [
        new SapExportStatusFilter(),
        new SapExportErrorCategoryFilter(),
        new CustomerIdentifierTypeFilter({ excludedTypes: [DmsSystem.ProfessionalWorks] }),
        new SapExportPeriodFilter(),
      ],
    [filters],
  );
  const body = useMemo(
    () =>
      buildCustomerSapExportSearchBody({
        externalBody,
        filterValues,
        dateFormat,
      }),
    [dateFormat, externalBody, filterValues],
  );

  const handleDelete = useCallback(
    async (customerSapExport: CustomerSapExport) => {
      if (!customerSapExport.uuid) {
        return;
      }

      const customerName = customerSapExport.customer
        ? getCustomerDisplayName(customerSapExport.customer)
        : customerSapExport.uuid;

      await toastMutationStatus({
        mutationFn: () => deleteMutation.mutateAsync({ uuid: customerSapExport.uuid }),
        successMessage: t("masterdata:SapExportStateDeleted", {
          name: customerName,
        }),
        errorMessage: t("masterdata:SapExportStateDeleteError", {
          name: customerName,
        }),
      });
    },
    [deleteMutation, t],
  );

  const queueReExports = useCallback(
    async (reExportBody: CustomerSapExportReExportBody): Promise<void> => {
      await toastMutationStatus({
        mutationFn: () => reExportMutation.mutateAsync(reExportBody),
        errorMessage: t("masterdata:CustomerExportsReExportError"),
        onSuccess: () => {
          toast.success(t("masterdata:CustomerExportsReExportQueued"));
        },
      });
    },
    [reExportMutation, t],
  );

  const hasReExportCriteria = hasCustomerSapExportSearchCriteria(body, search);

  const handleReExportAllMatching = useCallback(async (): Promise<void> => {
    // Guard against an unrestricted "re-export every customer" request; the backend rejects it as well.
    if (!hasReExportCriteria) {
      return;
    }
    await queueReExports({ force: false, filters: body, search: search || undefined });
  }, [body, hasReExportCriteria, queueReExports, search]);

  const customRowActions = useCallback(
    (row: { original: CustomerSapExport }): RowAction[] => {
      if (!row.original.customer) {
        return [];
      }

      return [
        {
          label: t("masterdata:CustomerDetails"),
          icon: <UserRoundIcon className="h-4 w-4" />,
          onClick: () => {
            const url = onRowNavigate(row.original);
            if (url) {
              navigate(url);
            }
          },
        },
      ];
    },
    [navigate, onRowNavigate, t],
  );

  // SAP export states are scoped through their customer: without company group scope the API returns nothing.
  if (!hasCompanyGroupScope) {
    return <CustomerCompanyGroupScopeEmptyState className={restProps.className} />;
  }

  return (
    <BaseCustomerSapExportTable
      searchPlaceholder={t("masterdata:SearchSapExports")}
      name={name}
      body={body}
      extend={extend}
      filters={defaultFilters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      onRowNavigate={onRowNavigate}
      onDelete={handleDelete}
      onSearchChange={handleSearchChange}
      headerComponent={
        canManageCustomer ? (
          <div className="flex items-center gap-2">
            {externalHeaderComponent}
            {hasReExportCriteria ? (
              <Button
                colorVariant={ButtonColorVariant.Secondary}
                disabled={reExportMutation.isPending}
                onClick={handleReExportAllMatching}
                variant={ButtonVariant.Outlined}
              >
                <CloudUploadIcon />
                {t("masterdata:ReExportAllMatchingCustomers")}
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-not-allowed" tabIndex={0} aria-disabled="true">
                    <Button colorVariant={ButtonColorVariant.Secondary} variant={ButtonVariant.Outlined} disabled>
                      <CloudUploadIcon />
                      {t("masterdata:ReExportAllMatchingCustomers")}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{t("masterdata:ReExportRequiresFilterOrSearch")}</TooltipContent>
              </Tooltip>
            )}
          </div>
        ) : (
          externalHeaderComponent
        )
      }
      customRowActions={customRowActions}
      confirmActions={["delete"]}
      confirmLabels={{
        delete: {
          title: t("masterdata:DeleteSapExportState"),
          text: t("common:ActionCannotBeUndone"),
          confirmButton: t("common:YesDeletePermanently"),
          cancelButton: t("common:Cancel"),
        },
      }}
      {...restProps}
    />
  );
}
